import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppHeader } from '@/components/AppHeader';
import { OfflineBanner } from '@/components/ui';
import { useOnline } from '@/lib/online';
import { useBackButton } from '@/components/MainButton';
import { ShopProvider, useShop } from '@/state';
import { isTelegram, startParam } from '@/lib/telegram';
import { decodeDeepLink, deepLinkToPath } from '@alkor/utils';
import { HomeScreen } from '@/screens/Home';
import { CategoryScreen } from '@/screens/Category';
import { ProductScreen } from '@/screens/Product';
import { SearchScreen } from '@/screens/Search';
import { CartScreen } from '@/screens/Cart';
import { CheckoutScreen } from '@/screens/Checkout';
import { OrderSuccessScreen, OrdersScreen } from '@/screens/Orders';
import { OrderDetailScreen } from '@/screens/OrderDetail';
import { FavoritesScreen, NotificationsScreen, NotFoundScreen } from '@/screens/Misc';

/** Экраны, на которых снизу живёт MainButton — им нужен запас по высоте. */
const WITH_MAIN_BUTTON = [/^\/p\//, /^\/cart$/, /^\/checkout$/, /^\/order\/[^/]+$/];

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useShop();
  const qc = useQueryClient();
  const online = useOnline();

  const isHome = location.pathname === '/';
  useBackButton(() => navigate(-1), !isHome);

  // Прокрутка сбрасывается при смене экрана — иначе новый экран открывается «в середине»
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const needsPadding = WITH_MAIN_BUTTON.some((re) => re.test(location.pathname)) && !isTelegram;

  return (
    <div className="app">
      <AppHeader />
      {/* Баннер вместо блокирующего экрана: уже загруженные данные остаются
          доступными, пользователь просто знает, что они могли устареть. */}
      {!online && <OfflineBanner onRetry={() => void qc.refetchQueries({ type: 'active' })} />}
      <main className={`app__body${needsPadding ? ' app__body--with-main-button' : ''}`}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/c/:slug" element={<CategoryScreen />} />
          <Route path="/sale" element={<CategoryScreen badge="sale" title="Акции" />} />
          <Route path="/p/:slug" element={<ProductScreen />} />
          <Route path="/search" element={<SearchScreen />} />
          <Route path="/cart" element={<CartScreen />} />
          <Route path="/checkout" element={<CheckoutScreen />} />
          <Route path="/order/:number" element={<OrderSuccessScreen />} />
          <Route path="/orders" element={<OrdersScreen />} />
          <Route path="/orders/:number" element={<OrderDetailScreen />} />
          <Route path="/favorites" element={<FavoritesScreen />} />
          <Route path="/settings/notifications" element={<NotificationsScreen />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </main>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed', left: 16, right: 16, bottom: 'calc(88px + var(--tg-safe-bottom))',
            zIndex: 40, background: 'var(--c-ink)', color: 'var(--c-on-ink)',
            padding: '12px 16px', borderRadius: 'var(--r-card)', fontSize: 13.5,
            lineHeight: 1.45, boxShadow: 'var(--sh-lift)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/** Разбор deep link: t.me/bot/app?startapp=p_deeper-pro-plus-2 открывает сразу товар. */
function DeepLinkGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useEffect(() => {
    const link = decodeDeepLink(startParam());
    if (link && link.kind !== 'utm') navigate(deepLinkToPath(link), { replace: true });
    // Разбирается один раз при старте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
}

export function App() {
  return (
    <ShopProvider>
      <DeepLinkGate>
        <Shell />
      </DeepLinkGate>
    </ShopProvider>
  );
}
